import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import Shell from "./components/Shell.jsx";
import SignIn from "./components/SignIn.jsx";
import Toaster from "./components/Toaster.jsx";
import { api } from "./lib/api.js";
import { LENS_META, RECALL } from "./lib/lenses.js";
import { createShell, loadSaved } from "./lib/shell.js";
import { recall, remember } from "./lib/storage.js";

// Each lens is its own chunk, so a visit loads only the lenses it opens (Field alone pulls in 3D).
const COMPONENTS = {
  today: lazy(() => import("./lenses/today/index.jsx")),
  explore: lazy(() => import("./lenses/explore/index.jsx")),
  field: lazy(() => import("./lenses/field/index.jsx")),
  focus: lazy(() => import("./lenses/focus/index.jsx")),
};

// Not exported: Fast Refresh needs this module to export components only.
const LENSES = LENS_META.map(l => ({ ...l, Component: COMPONENTS[l.id] }));
// Only owners open the director's editor, so it loads on first use; Recall and the explainer too.
const DirectorDrawer = lazy(() => import("./components/DirectorDrawer.jsx"));
const RecallPage = lazy(() => import("./components/RecallPage.jsx"));
const ExplainerDrawer = lazy(() => import("./components/ExplainerDrawer.jsx"));
const ScorecardDrawer = lazy(() => import("./components/ScorecardDrawer.jsx"));

function Notice({ title, children }) {
  return (
    <main className="app-notice">
      <h1>{title}</h1>
      {children && <p>{children}</p>}
    </main>
  );
}

export default function App() {
  const [tenant, setTenant] = useState(null);
  const [error, setError] = useState(null);

  // The tenant answer also says who is signed in; it is re-read after signing in or out, and
  // whenever the API reports that a session has ended.
  const refresh = useCallback(() => api.tenant().then(setTenant, setError), []);
  useEffect(() => {
    refresh();
    window.addEventListener("pot:signed-out", refresh);
    return () => window.removeEventListener("pot:signed-out", refresh);
  }, [refresh]);
  useEffect(() => {
    if (tenant) document.title = tenant.brand?.name || tenant.name;
  }, [tenant]);

  if (error) {
    return error.code === "unknown_host" ? (
      <Notice title="This address isn't set up">Nothing answers on {window.location.hostname} yet.</Notice>
    ) : (
      <Notice title="Can't reach the server">Check that the API is running, then reload.</Notice>
    );
  }
  if (!tenant) return <Notice title="Loading…" />;

  // A private tenant shows only the sign-in page until someone signs in (DEC-033), at whatever
  // address was asked for, so a deep link opens once sign-in is done.
  const signIn = <SignIn tenant={tenant} onSignedIn={refresh} />;
  const gated = tenant.access !== "public" && !tenant.user;

  return (
    <BrowserRouter>
      <Toaster />
      <Routes>
        <Route path="/signin" element={signIn} />
        {gated ? (
          <Route path="*" element={signIn} />
        ) : (
          <>
            <Route path="/" element={<ProjectList tenant={tenant} />} />
            <Route path="/p/:project" element={<ProjectView tenant={tenant} />} />
            <Route path="/p/:project/:lens" element={<ProjectView tenant={tenant} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
}

function ProjectList({ tenant }) {
  const [projects, setProjects] = useState(null);
  useEffect(() => {
    api.projects().then(setProjects, () => setProjects([]));
  }, []);

  return (
    <div className="app">
      <Shell tenant={tenant} />
      <main className="page">
        <h1 className="app-page-title">Projects</h1>
        {!projects ? (
          <p className="app-muted">Loading…</p>
        ) : projects.length === 0 ? (
          <p className="app-muted">No projects yet.</p>
        ) : (
          <ul className="app-project-list">
            {projects.map(p => (
              <li key={p.slug}>
                <Link to={`/p/${p.slug}`}>
                  <span className="app-project-name">{p.name}</span>
                  <span className="app-project-meta">
                    {p.mode === "practice" ? "Practice" : "Field"} · {p.latest_period ? `latest day ${p.latest_period}` : "no data yet"}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function ProjectView({ tenant }) {
  const { project: slug, lens } = useParams();
  const [project, setProject] = useState(null);
  const [day, setDay] = useState(undefined); // undefined while loading, null when the project has no day yet
  const [error, setError] = useState(null);
  const [dayVersion, setDayVersion] = useState(0); // bumped when a rebuild finishes
  const [directorOpen, setDirectorOpen] = useState(false);
  const [explaining, setExplaining] = useState(null); // the paper the explainer is open for (DEC-043)
  const [carding, setCarding] = useState(null); // the paper whose score card is open (DEC-008)

  useEffect(() => {
    setProject(null);
    setDay(undefined);
    setError(null);
    setDirectorOpen(false);
    setExplaining(null);
    setCarding(null);
    api.project(slug).then(setProject, setError);
  }, [slug]);
  useEffect(() => {
    api.day(slug).then(setDay, err => (err.code === "no_day" ? setDay(null) : setError(err)));
  }, [slug, dayVersion]);

  // Lenses ask for the director through the shell; only an owner's view opens it.
  const canEdit = Boolean(project?.canEdit);
  useEffect(() => {
    if (!canEdit) return undefined;
    const open = () => {
      setExplaining(null);
      setCarding(null);
      setDirectorOpen(true);
    };
    window.addEventListener("pot:director", open);
    return () => window.removeEventListener("pot:director", open);
  }, [canEdit]);
  const closeDirector = useCallback(() => setDirectorOpen(false), []);
  const showRebuilt = useCallback(() => setDayVersion(v => v + 1), []);
  // Any lens, or Recall, asks for the explainer or a score card; one drawer at a time.
  useEffect(() => {
    const explain = e => {
      setDirectorOpen(false);
      setCarding(null);
      setExplaining(e.detail);
    };
    const card = e => {
      setDirectorOpen(false);
      setExplaining(null);
      setCarding(e.detail);
    };
    window.addEventListener("pot:explain", explain);
    window.addEventListener("pot:scorecard", card);
    return () => {
      window.removeEventListener("pot:explain", explain);
      window.removeEventListener("pot:scorecard", card);
    };
  }, []);
  const closeExplainer = useCallback(() => setExplaining(null), []);
  const closeCard = useCallback(() => setCarding(null), []);

  // The project opens on the lens last used; Recall is a page someone goes to, not where a visit starts.
  useEffect(() => {
    if (lens && lens !== RECALL.id) remember(tenant.slug, slug, "lens", lens);
  }, [tenant.slug, slug, lens]);

  // Someone signed in gets their saved memory, library and goal switches (DEC-041); a visitor's
  // stay in this browser. If the account can't be read, this visit keeps them in the browser too,
  // and they move into the account next time.
  const [saved, setSaved] = useState(undefined); // undefined while loading, null when kept in the browser
  useEffect(() => {
    if (!tenant.user) {
      setSaved(null);
      return undefined;
    }
    let live = true;
    setSaved(undefined);
    loadSaved(tenant, { slug }).then(
      s => live && setSaved(s),
      () => {
        if (!live) return;
        setSaved(null);
        window.dispatchEvent(new CustomEvent("pot:toast", { detail: "Couldn't load your saved papers, so this visit keeps them in this browser." }));
      }
    );
    return () => {
      live = false;
    };
  }, [tenant, slug]);

  // One shell per project and day, shared by the top bar and whichever lens is open.
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;
  const shell = useMemo(
    () =>
      project && day && saved !== undefined
        ? createShell({ tenant, project, payload: day.payload, navigate: to => navRef.current(to), saved })
        : null,
    [tenant, project, day, saved]
  );
  useEffect(() => () => shell?.close(), [shell]);

  if (error) {
    return (
      <div className="app">
        <Shell tenant={tenant} />
        <Notice title={error.code === "unknown_project" ? "No such project" : "Something went wrong"}>
          <Link to="/">Back to projects</Link>
        </Notice>
      </div>
    );
  }
  if (!project) {
    return (
      <div className="app">
        <Shell tenant={tenant} />
        <main className="page">
          <p className="app-muted">Loading…</p>
        </main>
      </div>
    );
  }

  const allowed = project.config?.lenses || LENSES.map(l => l.id);
  const enabled = LENSES.filter(l => allowed.includes(l.id));
  const director = (
    <>
      {directorOpen && canEdit && (
        <Suspense fallback={null}>
          <DirectorDrawer project={project} onClose={closeDirector} onRebuilt={showRebuilt} />
        </Suspense>
      )}
      {explaining && (
        <Suspense fallback={null}>
          <ExplainerDrawer tenant={tenant} project={project} paper={explaining} onClose={closeExplainer} />
        </Suspense>
      )}
      {carding && (
        <Suspense fallback={null}>
          <ScorecardDrawer project={project} paper={carding} onClose={closeCard} />
        </Suspense>
      )}
    </>
  );

  if (lens === RECALL.id) {
    return (
      <div className="app" data-lens={RECALL.id}>
        <Shell tenant={tenant} project={project} lenses={enabled} day={day} shell={shell} lens={RECALL.id} />
        <Suspense
          fallback={
            <main className="page">
              <p className="app-muted">Loading…</p>
            </main>
          }
        >
          <RecallPage tenant={tenant} project={project} shell={shell} />
        </Suspense>
        {director}
      </div>
    );
  }

  const current = enabled.find(l => l.id === lens);
  if (!current) {
    const last = recall(tenant.slug, slug, "lens", enabled[0].id);
    const target = enabled.some(l => l.id === last) ? last : enabled[0].id;
    return <Navigate to={`/p/${slug}/${target}`} replace />;
  }

  const { Component } = current;
  return (
    <div className="app" data-lens={current.id}>
      <Shell tenant={tenant} project={project} lenses={enabled} day={day} shell={shell} lens={current.id} />
      {day === undefined || (day && !shell) ? (
        <main className="page">
          <p className="app-muted">Loading…</p>
        </main>
      ) : day === null ? (
        <main className="page">
          <section className="app-empty">
            <h2>Nothing to show yet</h2>
            <p>
              {project.name} has no day yet. One appears once its lanes deliver items and the daily build runs.
            </p>
            {canEdit && (
              <button type="button" className="app-linkbtn" onClick={() => setDirectorOpen(true)}>
                Set up the director
              </button>
            )}
          </section>
        </main>
      ) : (
        // Lenses are full-width; each one lays out its own content.
        <main className="lens-main">
          <Suspense fallback={<p className="page app-muted">Loading…</p>}>
            <Component shell={shell} project={project} tenant={tenant} />
          </Suspense>
        </main>
      )}
      {director}
    </div>
  );
}
